import React from 'react';
import { RadioGroup } from '@headlessui/react';
import tw from 'twin.macro';
import { CheckCircleIcon as CheckCircle } from '@heroicons/react/20/solid';
import { labelDesign } from '@teetsh/app/src/components/Form/Label';
import { surfaceBrandColorAsTextColorStyle } from 'components/colors';
import { TwStyle } from 'twin.macro';

interface Option<T> {
  value: T;
  title: string;
  description?: any;
}

interface Props<T> {
  cols?: Cols;
  label?: string;
  options: Option<T>[];
  value: T;
  onChange: (v: T) => void;
}

type Cols = '1' | '2' | '3' | '4' | '5' | '6';

const getGridCols: Record<Cols, TwStyle> = {
  '1': tw`sm:grid-cols-1`,
  '2': tw`sm:grid-cols-2`,
  '3': tw`sm:grid-cols-3`,
  '4': tw`sm:grid-cols-4`,
  '5': tw`sm:grid-cols-5`,
  '6': tw`sm:grid-cols-6`,
};

export default function Cards<T>(props: Props<T>) {
  const { options, value, onChange, label, cols = '3' } = props;
  return (
    <RadioGroup value={value} onChange={onChange}>
      {label && <RadioGroup.Label css={labelDesign}>{label}</RadioGroup.Label>}
      <div
        css={[tw`mt-2 grid grid-cols-1 gap-y-2 sm:gap-x-4`, getGridCols[cols]]}
      >
        {options.map(option => (
          <RadioGroup.Option
            key={option.title as string}
            value={option.value}
            css={[
              tw`relative bg-white rounded-lg shadow-sm p-4 flex cursor-pointer focus:outline-none`,
              tw`border border-neutral-quiet hover:border-brand-moderate`,
              value === option.value &&
                tw`ring-2 bg-brand-moderate ring-brand-intense border-transparent!`,
            ]}
          >
            {({ checked }) => (
              <>
                <div tw="flex-1 flex">
                  <div tw="flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      css={[
                        tw`block text-sm font-medium text-neutral-intense`,
                        checked && tw`text-brand-intense`,
                      ]}
                    >
                      {option.title}
                    </RadioGroup.Label>
                    <RadioGroup.Description
                      as="span"
                      css={[
                        tw`mt-1 flex items-center text-sm text-neutral-quiet`,
                        checked && tw`text-brand-moderate`,
                      ]}
                    >
                      {option.description}
                    </RadioGroup.Description>
                  </div>
                </div>
                <CheckCircle
                  css={[
                    surfaceBrandColorAsTextColorStyle,
                    !checked && tw`invisible`,
                    tw`h-5 w-5`,
                  ]}
                  aria-hidden="true"
                />
                <div
                  css={[
                    checked && tw`border-brand-intense`,
                    !checked && tw`border-transparent`,
                    tw`absolute -inset-px rounded-lg pointer-events-none`,
                  ]}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
}
